import React, { useState } from "react";
import { sanitizeResponse } from "../utils/sanitize";
import { scanOutputForIssues } from "../utils/serviceValidation";
import { LAMBDA_REGION, LAMBDA_FUNCTIONS } from "../config/lambdaTargets";
import {
  Container,
  SpaceBetween,
  Textarea,
  Button,
  FormField,
  ColumnLayout,
  StatusIndicator,
  Box,
  Modal,
  Alert,
} from "@cloudscape-design/components";

export default function SmartDiffInput() {
  const [leftText, setLeftText] = useState("");
  const [rightText, setRightText] = useState("");
  const [outputData, setOutputData] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [compareStatus, setCompareStatus] = useState(null); // null, 'success', 'error'
  const [showWarningModal, setShowWarningModal] = useState(false);
  const [findings, setFindings] = useState([]);

  // Function to perform text comparison using Claude 3.7
  const handleCompare = async () => {
    // Check if both fields are empty
    if (!leftText.trim() && !rightText.trim()) {
      setShowWarningModal(true);
      return;
    }

    // Check if only one field has content
    if (!leftText.trim() || !rightText.trim()) {
      setShowWarningModal(true);
      return;
    }

    setIsLoading(true);
    setCompareStatus(null);
    setFindings([]);
    
    try {
      
      // Call Lambda directly to avoid AppSync 29-second timeout
      const { fetchAuthSession } = await import('aws-amplify/auth');
      const { LambdaClient, InvokeCommand } = await import('@aws-sdk/client-lambda');
      const session = await fetchAuthSession();
      const lambda = new LambdaClient({ region: LAMBDA_REGION, credentials: session.credentials });
      const resp = await lambda.send(new InvokeCommand({
        FunctionName: LAMBDA_FUNCTIONS.smartDiff,
        Payload: JSON.stringify({ arguments: { beforeModification: leftText.trim(), afterModification: rightText.trim() } }),
      }));
      const result = JSON.parse(new TextDecoder().decode(resp.Payload));
      if (result.errorMessage) throw new Error(result.errorMessage);
      if (result.statusCode === 200) {
        const sanitized = sanitizeResponse(result.body);
        setOutputData(sanitized);
        setFindings(scanOutputForIssues(sanitized));
        setCompareStatus('success');
      } else {
        throw new Error(result.body || `Lambda returned status ${result.statusCode}`);
      }
      
      // Clear success status after 3 seconds
      setTimeout(() => setCompareStatus(null), 3000);
    } catch (error) {
      console.error("Error during SmartDiff comparison:", error);
      setOutputData(`An error occurred during AWS question comparison: ${error.message || 'Please try again.'}`);
      setCompareStatus('error');
      setTimeout(() => setCompareStatus(null), 3000);
    } finally {
      setIsLoading(false);
    }
  };

  // Function to clear all inputs
  const handleClear = () => {
    setLeftText("");
    setRightText("");
    setOutputData("");
    setCompareStatus(null);
    setFindings([]);
  };

  return (
    <Container>
      <SpaceBetween size="l">
        {/* Header */}
        <Box variant="h2">SmartDiff - AWS Question Comparison Tool</Box>
        
        {/* Side-by-side input areas */}
        <ColumnLayout columns={2} variant="text-grid">
          <FormField label="Before Modification" stretch={true}>
            <Textarea
              value={leftText}
              onChange={({ detail }) => setLeftText(detail.value)}
              placeholder="Enter your original AWS question here for comparison..."
              rows={18}
            />
          </FormField>
          
          <FormField label="After Modification" stretch={true}>
            <Textarea
              value={rightText}
              onChange={({ detail }) => setRightText(detail.value)}
              placeholder="Enter your modified AWS question here for comparison..."
              rows={18}
            />
          </FormField>
        </ColumnLayout>

        {/* Swap button */}
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <Button
            iconName="refresh"
            onClick={() => { const tmp = leftText; setLeftText(rightText); setRightText(tmp); }}
            disabled={isLoading}
          >
            Swap
          </Button>
        </div>

        {/* Action buttons */}
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '10px' }}>
          <Button
            variant="primary"
            onClick={handleCompare}
            loading={isLoading}
            disabled={isLoading}
          >
            {isLoading ? "Comparing..." : "Compare"}
          </Button>
          
          <Button
            variant="normal"
            onClick={handleClear}
            disabled={isLoading}
          >
            Clear All
          </Button>
          
          {/* Status indicator */}
          {compareStatus === 'success' && (
            <StatusIndicator type="success">
              Comparison completed successfully
            </StatusIndicator>
          )}
          
          {compareStatus === 'error' && (
            <StatusIndicator type="error">
              Comparison failed
            </StatusIndicator>
          )}
        </div>

        {/* Output area */}
        <FormField 
          label={
            <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
              <span>Comparison Results</span>
              {outputData && (
                <Button
                  variant="inline-link"
                  onClick={() => navigator.clipboard.writeText(outputData)}
                  aria-label="Copy comparison results to clipboard"
                >
                  Copy Results
                </Button>
              )}
            </div>
          }
          stretch={true}
        >
          <Textarea
            value={outputData}
            readOnly={true}
            placeholder="Comparison results will appear here..."
            rows={15}
          />
        </FormField>

        {findings.length > 0 && !isLoading && (
          <Alert
            type="warning"
            header={`${findings.length} potential issue${findings.length === 1 ? "" : "s"} detected in the comparison`}
          >
            <ul style={{ marginTop: 0, marginBottom: 0, paddingLeft: "1.5em" }}>
              {findings.map((f, i) => (
                <li key={i}>{f.message}</li>
              ))}
            </ul>
          </Alert>
        )}
      </SpaceBetween>

      {/* Warning Modal */}
      <Modal
        onDismiss={() => setShowWarningModal(false)}
        visible={showWarningModal}
        header="Missing Question Content"
        closeAriaLabel="Close modal"
        footer={
          <Box float="right">
            <Button variant="primary" onClick={() => setShowWarningModal(false)}>
              OK
            </Button>
          </Box>
        }
      >
        <Alert type="warning" header="Both questions are required">
          <p>
            To perform a meaningful comparison, please enter content in both text areas:
          </p>
          <ul>
            <li><strong>Before Modification:</strong> Enter your original AWS question</li>
            <li><strong>After Modification:</strong> Enter your modified AWS question</li>
          </ul>
          <p>
            Both questions should include the complete question text with all answer options (A, B, C, D) for the best analysis results.
          </p>
        </Alert>
      </Modal>
    </Container>
  );
}
